<template>
  <div class="flex flex-wrap gap-4 text-sm">
    <div class="flex flex-col gap-2 flex-1 w-full">
      <p class="font-semibold">{{ t('Date') }}</p>

      <div class="flex border gap-2 rounded-lg px-3 py-2 items-center">
        <DatePicker
          :model-value="selectedDate"
          @update:model-value="(date) => emit('update:selectedDate', date)"
        />

        <VTextField
          :model-value="selectedDateFormatted"
          :class="['date-input', { 'text-error-600': isError }]"
          :variant="'solo'"
          hide-details
          @blur="onBlur"
          @update:model-value="onDateUpdate"
        />
      </div>
    </div>

    <div class="flex flex-col gap-2 flex-1 w-full">
      <p class="font-semibold">{{ t('Time') }}</p>

      <VMenu :location="'bottom center'" :offset="6" :max-height="248">
        <template #activator="{ props, isActive }">
          <div
            v-bind="props"
            class="flex border gap-2 rounded-lg px-3 py-2 items-center justify-between"
          >
            <div class="flex items-center gap-1">
              <TimePicker
                class="text-base"
                :time-limit="
                  selectedTimeInSeconds ? secondsToTime(selectedTimeInSeconds.toString()) : '0'
                "
                :is-error="isError"
                :format="timeFormat"
                density="compact"
                @click.stop.prevent
                @update:model-value="(value) => emit('update:selectedTime', timeToMinutes(value))"
              />

              <span
                v-if="dayPeriod"
                data-testid="time-day-period"
                :class="['text-base', { 'text-error-600': isError }]"
              >
                {{ dayPeriod }}
              </span>
            </div>

            <VIcon
              data-testid="time-picker-icon"
              :color="'grey-400'"
              :icon="isActive ? 'expand_less' : 'expand_more'"
            />
          </div>
        </template>

        <VList data-testid="time-options-list">
          <VListItem
            v-for="option in timeOptions"
            :class="{ 'bg-grey-200': option.value === selectedTime }"
            :key="option.value"
            :title="option.title"
            :value="option.value"
            @click="emit('update:selectedTime', option.value)"
          />
        </VList>
      </VMenu>
    </div>
  </div>
</template>

<script setup lang="ts">
import DatePicker from '@/components/common/DatePicker.vue'
import { useI18n } from 'vue-i18n'
import TimePicker from '@/hospitalAdminTool/components/common/TimePicker.vue'
import {
  secondsToTime,
  timeToMinutes
} from '@/hospitalAdminTool/settings/utils/routingSettings.utils'
import type { TimeOption } from '@/composables/useDetailsFormBase'
import { NUMBER_OF_SECONDS_IN_ONE_MINUTE } from '@/util/constants'
import { computed, ref } from 'vue'

interface Props {
  selectedDate: Date
  selectedTime: number | null
  timeOptions: TimeOption[]
  isError: boolean
}

interface Emits {
  (e: 'update:selectedDate', value: Date): void
  (e: 'update:selectedTime', value: number): void
}

const { t, locale } = useI18n()

const timeFormat = computed<'24hr' | 'ampm'>(() => (locale.value === 'en' ? 'ampm' : '24hr'))

const { selectedTime, selectedDate } = defineProps<Props>()
const emit = defineEmits<Emits>()

const temporaryInputData = ref<string>('')

const selectedTimeInSeconds = computed<number | null>(() => {
  if (selectedTime) {
    return selectedTime * NUMBER_OF_SECONDS_IN_ONE_MINUTE
  }

  return null
})

const dayPeriod = computed<string>(() => {
  if (timeFormat.value !== 'ampm' || selectedTime === null) return ''

  const date = new Date()
  date.setHours(Math.floor(selectedTime / 60), selectedTime % 60, 0, 0)

  const parts = new Intl.DateTimeFormat(locale.value, {
    hour: 'numeric',
    hour12: true
  }).formatToParts(date)

  return parts.find((part) => part.type === 'dayPeriod')?.value ?? ''
})

const selectedDateFormatted = computed(() => {
  const day = selectedDate.getDate().toString().padStart(2, '0')
  const month = (selectedDate.getMonth() + 1).toString().padStart(2, '0')
  const year = selectedDate.getFullYear()

  return `${day}.${month}.${year}`
})

function onDateUpdate(date: string): void {
  temporaryInputData.value = date
}

function onBlur(): void {
  const [day, month, year] = temporaryInputData.value.split('.')
  const parsedDay = Number(day)
  const parsedMonth = Number(month)
  const parsedYear = Number(year)

  if (
    isNaN(parsedDay) ||
    isNaN(parsedMonth) ||
    isNaN(parsedYear) ||
    parsedDay > 31 ||
    parsedMonth > 12 ||
    parsedYear.toString().length > 4
  ) {
    temporaryInputData.value = ''
    return
  }

  const dateCopy = new Date(selectedDate)
  dateCopy.setFullYear(parsedYear, parsedMonth - 1, parsedDay)

  emit('update:selectedDate', dateCopy)
}
</script>

<style scoped lang="scss">
.date-input {
  :deep(.v-field) {
    box-shadow: none;
    color: inherit;
  }

  :deep(input) {
    padding: 0;
    min-height: unset;
  }
}
</style>
